import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ClassEntriesOverview } from '@/components/entries'
import { PageHeading } from '@/components/page-heading'
import { RecentCheckIns } from '@/components/entries/RecentCheckIns'
import { useCurrentShow } from '@/context/current-show'
import { useEntryIntakeManager, type CheckedInExhibit } from '@/hooks/useEntryIntake'
import { useActorPermissions, formatRoleList } from '@/hooks/useActorPermissions'
import { useAllocateEntryCodes, useCreateEntry } from '@/api/hooks'
import { usePendingEntrySync } from '@/hooks/usePendingEntrySync'
import type { TEntry } from '@schemas'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'

export default function EntriesPage() {
  const { currentShow } = useCurrentShow()
  const navigate = useNavigate()
  const currentSlug = useShowSlug()
  const intake = useEntryIntakeManager(currentShow?.id)
  const allocateCodes = useAllocateEntryCodes()
  const createEntry = useCreateEntry()
  const permissions = useActorPermissions(currentShow?.id)

  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  )
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }
  }, [])

  const permissionLoading = permissions.staffQuery?.isLoading ?? false
  const canManageEntries = permissions.can('entries:write')
  const canAllocateCodes = permissions.can('entrycodes:allocate')

  const entriesRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('entries:write')),
    [permissions]
  )
  const allocateRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('entrycodes:allocate')),
    [permissions]
  )

  const entryPermissionDisabled = permissionLoading || !canManageEntries
  const entryPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canManageEntries
      ? `Creating entries requires ${entriesRolesLabel}.`
      : undefined
  const allocationPermissionDisabled = permissionLoading || !canAllocateCodes
  const allocationPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canAllocateCodes
      ? `Fetching Exhibit IDs requires ${allocateRolesLabel}.`
      : undefined

  const handleEntrySyncSuccess = useCallback(
    async (
      entry: TEntry,
      options: {
        usedCode: string
        originalCode?: string | null
        className?: string | null
        classId?: string
      }
    ) => {
      await intake.clearLastClaimed()
      const canonicalCode = entry.entryCode ?? options.usedCode
      const originalPersisted =
        options.originalCode && options.originalCode !== canonicalCode
          ? options.originalCode
          : options.usedCode.startsWith('TMP') && options.usedCode !== canonicalCode
            ? options.usedCode
            : undefined
      const exhibitorId = entry.exhibitorId ?? null
      if (exhibitorId) {
        await intake.recordRecentEntry({
          id: entry.id ?? `${exhibitorId}:${canonicalCode}`,
          entryCode: canonicalCode,
          exhibitorId,
          className: options.className ?? null,
          originalCode: originalPersisted ?? null,
        })
      }
      const mapping =
        originalPersisted && originalPersisted !== canonicalCode
          ? `${originalPersisted} → ${canonicalCode}`
          : null
      toast.success(
        mapping ? `Entry ${canonicalCode} created (${mapping})` : `Entry ${canonicalCode} created`
      )
    },
    [intake]
  )

  const { syncPendingEntries, isSyncing } = usePendingEntrySync({
    intake,
    currentShowId: currentShow?.id ?? null,
    currentShowStatus: currentShow?.status ?? null,
    isOnline,
    entryPermissionDisabled,
    entryPermissionReason,
    allocationPermissionDisabled,
    allocationPermissionReason,
    createEntry,
    allocateCodes,
    onEntryCreated: (entry, info) =>
      handleEntrySyncSuccess(entry, {
        usedCode: info.usedCode,
        originalCode: info.originalCode,
        className: info.className,
        classId: info.classId,
      }),
  })

  const [syncDisabledReason, syncDisabledBase] = useMemo(() => {
    if (entryPermissionDisabled) {
      return [entryPermissionReason, true] as const
    }
    if (!isOnline) {
      return ['Unavailable while offline', true] as const
    }
    if (createEntry.isPending) {
      return ['Entry submission in progress', true] as const
    }
    if (allocateCodes.isPending) {
      return ['Code allocation in progress', true] as const
    }
    return [undefined, false] as const
  }, [
    allocateCodes.isPending,
    createEntry.isPending,
    entryPermissionDisabled,
    entryPermissionReason,
    isOnline,
  ])

  const syncDisabled = syncDisabledBase || isSyncing
  const hasPendingEntries = intake.pendingEntries.length > 0
  const showSyncNowPrompt = isOnline && hasPendingEntries

  const handleSyncPending = useCallback(() => {
    void syncPendingEntries(intake.pendingEntries)
  }, [intake.pendingEntries, syncPendingEntries])

  const handleCopyExhibitId = useCallback(async (entryCode: string) => {
    if (!entryCode) return
    if (typeof navigator === 'undefined') {
      toast.error('Copy is unavailable in this environment.')
      return
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(entryCode)
      } else {
        throw new Error('Clipboard API unavailable')
      }
      toast.success('Exhibit ID copied')
    } catch {
      toast.error('Unable to copy Exhibit ID. Copy it manually.')
    }
  }, [])

  const showAllowsCheckIn = currentShow?.status === 'accepting_entries'

  const handleRecentCopy = useCallback(
    (entryCode: string | null | undefined) => {
      if (!entryCode) return
      void handleCopyExhibitId(entryCode)
    },
    [handleCopyExhibitId]
  )

  const handleOpenRecentEntry = useCallback(
    (event: CheckedInExhibit) => {
      if (!event.exhibitorId) {
        toast.error('Entry details unavailable. Refresh and try again.')
        return
      }
      navigate(showAware('/check-in', { currentSlug }), {
        state: { exhibitorId: event.exhibitorId },
      })
    },
    [currentSlug, navigate]
  )

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <PageHeading>Entries Overview</PageHeading>
        <p className="text-muted-foreground">
          Review class rosters, entry statistics, and the latest check-ins for the active show.
        </p>
      </header>

      {showAllowsCheckIn && (
        <RecentCheckIns
          events={intake.checkedInExhibits}
          showId={currentShow?.id ?? null}
          listIdPrefix="entries-recent-checkins"
          showSyncNowPrompt={showSyncNowPrompt}
          onSyncPending={handleSyncPending}
          syncDisabled={syncDisabled}
          syncDisabledReason={syncDisabledReason}
          onCopyExhibitId={handleRecentCopy}
          onOpenEntry={handleOpenRecentEntry}
        />
      )}

      <ClassEntriesOverview />
    </div>
  )
}
